// ATE-CR-117
import { useEffect, useState } from "react";

import { listCreditHolds, type CreditHold } from "@/api/credits.api";

interface Props {
  readonly tenantId: string;
}

// ATE-CR-117
export function HoldsTab({ tenantId }: Props): JSX.Element {
  const [holds, setHolds] = useState<CreditHold[]>([]);
  const [activeOnly, setActiveOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);

    listCreditHolds(tenantId, { activeOnly, signal: ctrl.signal })
      .then((data) => {
        if (cancelled) return;
        setHolds(data);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [tenantId, activeOnly]);

  return (
    <div className="holds-tab">
      <div className="credits-tab__filter">
        <button
          type="button"
          className={`btn btn--sm ${activeOnly ? "btn--primary" : "btn--ghost"}`}
          aria-pressed={activeOnly}
          onClick={() => setActiveOnly(true)}
        >
          Active only
        </button>
        <button
          type="button"
          className={`btn btn--sm ${!activeOnly ? "btn--primary" : "btn--ghost"}`}
          aria-pressed={!activeOnly}
          onClick={() => setActiveOnly(false)}
        >
          All
        </button>
      </div>

      {loading && (
        <div role="status" className="credits-tab__loading">Loading holds…</div>
      )}

      {error && (
        <div role="alert" className="credits-tab__error">
          Failed to load holds: {error.message}
        </div>
      )}

      {!loading && !error && (
        <table className="credits-table">
          <thead>
            <tr>
              <th scope="col">Resource</th>
              <th scope="col">Amount</th>
              <th scope="col">Source</th>
              <th scope="col">Created</th>
              <th scope="col">Expires</th>
              <th scope="col">Resolution</th>
            </tr>
          </thead>
          <tbody>
            {holds.map((hold) => {
              // ATE-CR-117 — task 11 FIX: backend `resolution` is null for ACTIVE
              // holds; map null → "ACTIVE" for the badge label.
              const status = hold.resolution ?? "ACTIVE";
              return (
                <tr key={hold.id}>
                  <td>{hold.resource}</td>
                  <td>{hold.amount}</td>
                  <td>{hold.source}</td>
                  <td>{new Date(hold.created_at).toLocaleString()}</td>
                  <td>{new Date(hold.expires_at).toLocaleString()}</td>
                  <td>
                    <span
                      className={`badge badge--hold-${status.toLowerCase()}`}
                    >
                      {status}
                    </span>
                  </td>
                </tr>
              );
            })}
            {holds.length === 0 && (
              <tr>
                <td colSpan={6} className="credits-table__empty">
                  No holds found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
